import { useEffect, useRef, useState } from "react";
import { X, Flashlight, Keyboard } from "lucide-react";
import { Button } from "@/components/ui/button";

// Scanner service abstraction: native BarcodeDetector first, ZXing fallback. Replaceable by a native app scanner later.
export function Scanner({ open, onClose, onDetect, title = "Point the camera at the product barcode" }) {
  const video = useRef(null);
  const stop = useRef(() => {});
  const busy = useRef(false);
  const [err, setErr] = useState("");
  const [manual, setManual] = useState("");
  const [torch, setTorch] = useState(false);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    busy.current = false;
    setErr("");
    const hit = (code) => {
      if (busy.current || !code) return;
      busy.current = true;
      setFlash(true); setTimeout(() => setFlash(false), 250);
      navigator.vibrate?.(60);
      onDetect(String(code));
      setTimeout(() => { busy.current = false; }, 1500);
    };
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } } });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        video.current.srcObject = stream;
        await video.current.play();
        stop.current = () => stream.getTracks().forEach((t) => t.stop());
        if ("BarcodeDetector" in window) {
          const det = new window.BarcodeDetector({ formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "qr_code"] });
          const loop = async () => {
            if (cancelled) return;
            try { const r = await det.detect(video.current); if (r[0]) hit(r[0].rawValue); } catch (e) { /* frame not ready */ }
            setTimeout(loop, 250);
          };
          loop();
        } else {
          const { BrowserMultiFormatReader } = await import("@zxing/browser");
          const reader = new BrowserMultiFormatReader();
          const ctl = await reader.decodeFromVideoElement(video.current, (res) => res && hit(res.getText()));
          const prev = stop.current;
          stop.current = () => { ctl.stop(); prev(); };
        }
      } catch (e) { setErr("Camera unavailable — enter the barcode or search instead."); }
    })();
    return () => { cancelled = true; stop.current(); };
  }, [open, onDetect]);
  const toggleTorch = async () => {
    const track = video.current?.srcObject?.getVideoTracks?.()[0];
    try { await track.applyConstraints({ advanced: [{ torch: !torch }] }); setTorch(!torch); } catch (e) { setErr("Flashlight isn't supported on this device."); }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black text-white flex flex-col" data-testid="scanner">
      <div className="flex items-center justify-between p-4"><span className="text-sm">{title}</span>
        <button onClick={onClose} className="p-2" aria-label="Close scanner" data-testid="scanner-close"><X className="h-6 w-6" /></button></div>
      <div className="relative flex-1 overflow-hidden">
        <video ref={video} className="absolute inset-0 w-full h-full object-cover" playsInline muted />
        <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-40 border-2 rounded-xl transition-colors ${flash ? "border-emerald-400 bg-emerald-400/20" : "border-white/80"}`} />
        {err && <div className="absolute bottom-4 inset-x-4 rounded-lg bg-black/70 p-3 text-sm" data-testid="scanner-error">{err}</div>}
      </div>
      <div className="p-4 space-y-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (manual) { onDetect(manual); setManual(""); } }}>
          <div className="relative flex-1"><Keyboard className="h-4 w-4 absolute left-3 top-3.5 text-stone-400" />
            <input className="w-full h-11 rounded-lg bg-white/10 pl-9 pr-3 text-white placeholder:text-stone-400" inputMode="numeric" placeholder="Enter barcode or search" value={manual} onChange={(e) => setManual(e.target.value)} data-testid="scanner-manual" /></div>
          <Button type="submit" className="h-11 bg-white text-black hover:bg-stone-200" data-testid="scanner-manual-go">Go</Button>
          <Button type="button" variant="outline" className="h-11 bg-transparent text-white" onClick={toggleTorch} aria-label="Flashlight" data-testid="scanner-torch"><Flashlight className="h-4 w-4" /></Button>
        </form>
      </div>
    </div>
  );
}
